import { useState, useEffect } from 'react';
import { api, type DashboardUser, type Role, hasPermission, isSuperAdmin } from '../lib/api';
import { useAuth } from '../lib/auth';
import { UserPlus, Edit, Trash2, RotateCcw, Copy, Check } from 'lucide-react';

export function UserManagementPage() {
  const { me } = useAuth();
  const perms = me?.permissions;
  const [users, setUsers] = useState<DashboardUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create-User Dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createRoleId, setCreateRoleId] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // Edit-User Dialog
  const [editUser, setEditUser] = useState<DashboardUser | null>(null);
  const [editRoleId, setEditRoleId] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [resetResult, setResetResult] = useState('');

  const canCreate = hasPermission(perms, 'admin.users.create');
  const canEdit = hasPermission(perms, 'admin.users.edit');
  const canDelete = hasPermission(perms, 'admin.users.delete');

  async function loadData() {
    try {
      setLoading(true);
      const [u, r] = await Promise.all([
        api.get<{ users: DashboardUser[] }>('/admin/users'),
        api.get<{ roles: Role[] }>('/admin/roles'),
      ]);
      setUsers(u.users);
      setRoles(r.roles);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadData(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post<{ user: { id: string }; initialPassword: string }>(
        '/admin/users',
        { email: createEmail, roleId: createRoleId },
      );
      setCreatedPassword(res.initialPassword);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    setError('');
    try {
      await api.put(`/admin/users/${editUser.id}`, {
        roleId: editRoleId,
        status: editStatus,
      });
      setEditUser(null);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  async function handleResetPassword(userId: string) {
    try {
      const res = await api.put<{ newPassword?: string }>(`/admin/users/${userId}`, {
        resetPassword: true,
      });
      if (res.newPassword) setResetResult(res.newPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  async function handleDelete(user: DashboardUser) {
    if (!confirm(`Benutzer "${user.email}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  function copyToClipboard(text: string) {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    locked: 'bg-amber-100 text-amber-700',
    disabled: 'bg-red-100 text-red-700',
  };

  if (loading) return <div className="p-6 text-slate-500">Lade Benutzer...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard-Benutzer</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} Benutzer</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setShowCreate(true); setCreateEmail(''); setCreateRoleId(roles[0]?.id ?? ''); setCreatedPassword(''); }}
            className="btn btn-primary flex items-center gap-2"
          >
            <UserPlus size={16} /> Neuer Benutzer
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Passwort-Anzeige nach Reset */}
      {resetResult && (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-lg">
          <p className="text-sm font-medium text-amber-800">Neues Passwort (einmalig!):</p>
          <div className="flex items-center gap-2 mt-1">
            <code className="bg-white px-3 py-1 rounded border text-sm font-mono">{resetResult}</code>
            <button onClick={() => copyToClipboard(resetResult)} className="text-amber-600 hover:text-amber-800">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          <button onClick={() => setResetResult('')} className="text-xs text-amber-600 mt-2 underline">Schließen</button>
        </div>
      )}

      {/* User-Tabelle */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 font-medium text-slate-600">E-Mail</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Rolle</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">2FA</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Letzter Login</th>
              {(canEdit || canDelete) && <th className="text-right px-4 py-3 font-medium text-slate-600">Aktionen</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{u.email}</td>
                <td className="px-4 py-3">{u.role.name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[u.status] ?? ''}`}>
                    {u.status}
                  </span>
                </td>
                <td className="px-4 py-3">{u.totpEnabled ? '✓' : '–'}</td>
                <td className="px-4 py-3 text-slate-500">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('de-DE') : '–'}
                </td>
                {(canEdit || canDelete) && (
                  <td className="px-4 py-3 text-right space-x-1">
                    {canEdit && (
                      <>
                        <button
                          onClick={() => { setEditUser(u); setEditRoleId(u.role.id); setEditStatus(u.status); }}
                          className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                          title="Bearbeiten"
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          onClick={() => void handleResetPassword(u.id)}
                          className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                          title="Passwort zurücksetzen"
                        >
                          <RotateCcw size={15} />
                        </button>
                      </>
                    )}
                    {canDelete && u.id !== me?.id && (
                      <button
                        onClick={() => void handleDelete(u)}
                        className="p-1.5 rounded hover:bg-red-100 text-red-500"
                        title="Löschen"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create-Dialog */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => !createdPassword && setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            {createdPassword ? (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-800">Benutzer erstellt</h2>
                <p className="text-sm text-slate-600">Initial-Passwort (wird nur einmal gezeigt!):</p>
                <div className="flex items-center gap-2">
                  <code className="bg-slate-100 px-3 py-2 rounded border text-sm font-mono flex-1">{createdPassword}</code>
                  <button onClick={() => copyToClipboard(createdPassword)} className="btn btn-primary px-3 py-2">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <button onClick={() => setShowCreate(false)} className="btn btn-primary w-full">Schließen</button>
              </div>
            ) : (
              <form onSubmit={(e) => void handleCreate(e)} className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-800">Neuer Dashboard-Benutzer</h2>
                <div>
                  <label className="label">E-Mail</label>
                  <input type="email" required value={createEmail} onChange={(e) => setCreateEmail(e.target.value)}
                    className="input" placeholder="name@example.de" />
                </div>
                <div>
                  <label className="label">Rolle</label>
                  <select value={createRoleId} onChange={(e) => setCreateRoleId(e.target.value)} className="input">
                    {roles.filter((r) => isSuperAdmin(perms) || !r.permissions.includes('*')).map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowCreate(false)} className="btn">Abbrechen</button>
                  <button type="submit" className="btn btn-primary">Erstellen</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit-Dialog */}
      {editUser && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={(e) => void handleEdit(e)} className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-800">Benutzer bearbeiten</h2>
              <p className="text-sm text-slate-500">{editUser.email}</p>
              <div>
                <label className="label">Rolle</label>
                <select value={editRoleId} onChange={(e) => setEditRoleId(e.target.value)} className="input">
                  {roles.filter((r) => isSuperAdmin(perms) || !r.permissions.includes('*')).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="input">
                  <option value="active">Aktiv</option>
                  <option value="locked">Gesperrt</option>
                  <option value="disabled">Deaktiviert</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setEditUser(null)} className="btn">Abbrechen</button>
                <button type="submit" className="btn btn-primary">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
